/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable no-duplicate-imports */
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { IDynamicImagesData } from '../../themes/mattressfirm/definition-extensions/content-block.ext.props.autogenerated';
import LinksComponent from './components/links';
import { ISingleCategoryBannerData } from './single-category-banner.data';
import { actionableRegion as region, ILinksData, ISingleCategoryBannerProps } from './single-category-banner.props.autogenerated';

export interface IContentBlockViewProps extends ISingleCategoryBannerProps<{}> {
  title?: React.ReactNode;
  text?: React.ReactNode;
  image?: React.ReactNode;
  links?: ILinksData[];
  className?: string;
  contentBlockContainer: IModuleProps;
  imageContainer: INodeProps;
  detailsContainer: INodeProps;
  unbxdBannerHTML?: string;
  unbxdImageURL?: string;
  contentBlockAnchorTag?: INodeProps;
  useSeoData?: boolean;
  seoData?: any,
  useDynamicImages?: boolean;
  dynamicImages?: IDynamicImagesData;
  imageLink?: string;
  imageAriaLabel?: string;
  showServerSideBanner: boolean;
  telemetryContent?: ITelemetryContent;
  handleTextChange?(): (event: Msdyn365.ContentEditableEvent) => string;
}

interface ISingleCategoryBannerState {
  showServerSideBanner: boolean;
}

/**
 *
 * ContentCard component
 * @extends {React.PureComponent<ISingleCategoryBannerProps{}>}
 */

class ContentBlock extends React.PureComponent<ISingleCategoryBannerProps<ISingleCategoryBannerData>, ISingleCategoryBannerState> {
  private telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
    this.props.friendlyName,
    this.props.telemetry);

  constructor(props: ISingleCategoryBannerProps<ISingleCategoryBannerData>) {
    super(props);
    this.state = {
      showServerSideBanner: false
    };
  }
  private handleSkeletonLoading = () => {
    if (MsDyn365.isBrowser) {
      try {
        const bannerImage: HTMLCollectionOf<Element | any> = document.getElementsByClassName("single-category-banner-image");
        const bannerImagesSkeleton: HTMLElement | null = document.querySelector(".single-category-banner-skeleton");
        const bannerImageSelector: HTMLElement | null = document.querySelector(".single-category-banner-image");
        if (bannerImagesSkeleton && bannerImageSelector && bannerImage[0]?.complete && bannerImage[0]?.naturalHeight !== 0) {
          bannerImagesSkeleton.style.display = 'none';
          bannerImageSelector.style.display = 'block';
        } else if (bannerImagesSkeleton) {
          bannerImagesSkeleton.style.display = 'none';
          if (bannerImageSelector) {
            bannerImageSelector.style.display = 'block';
          }
        }
      } catch (error) {
        console.error('error[handleSkeletonLoading]', error);
      }
    }

  };
  public componentDidMount(): void {
    const activeProducts = (this.props.data?.listPageState?.result && this.props.data?.listPageState?.result?.activeProducts) || [];
    const extProperties = activeProducts && activeProducts.length > 0 && activeProducts[0]?.ExtensionProperties;
    const unbxdImageURL = extProperties && extProperties?.find(obj => obj?.Key === 'ImageURL')?.Value?.StringValue;
    const unbxdBannerHTMLRaw = extProperties && extProperties?.find(obj => obj?.Key === 'BannerHTML')?.Value?.StringValue;
    if (!(unbxdBannerHTMLRaw && unbxdImageURL)) {
      this.setState({ showServerSideBanner: true });
    }
  }

  // tslint:disable-next-line: cyclomatic-complexity max-func-body-length
  public render(): JSX.Element | null {
    const {
      heading,
      paragraph,
      image,
      links,
      className,
      useDynamicImages,
      dynamicImages,
      imageAriaLabel,
      useSeoData
    } = this.props.config;
    const activeProducts = (this.props.data?.listPageState?.result && this.props.data?.listPageState?.result?.activeProducts) || [];
    const activeFilters = (this.props.data?.listPageState?.result && this.props.data?.listPageState?.result?.activeFilters) || [];
    const isShowUnbxdBanner = activeFilters?.length > 1 ? false : true;
    const extProperties = activeProducts && activeProducts.length > 0 && activeProducts[0]?.ExtensionProperties;
    const unbxdImageURL = extProperties && extProperties?.find(obj => obj?.Key === 'ImageURL')?.Value?.StringValue;
    const unbxdLandingURL = extProperties && extProperties?.find(obj => obj?.Key === 'LandingURL')?.Value?.StringValue;
    const unbxdBannerHTMLRaw = extProperties && extProperties?.find(obj => obj?.Key === 'BannerHTML')?.Value?.StringValue;
    this.handleSkeletonLoading();
    const contentBlockTitle = heading && (
      <Msdyn365.Text
        className='ms-content-block__title'
        tag={heading.tag || 'h1'}
        text={heading.text}
        editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
      />
    );
    const imageProps = {
      gridSettings: this.props.context.request.gridSettings || {},
      imageSettings: image && image.imageSettings
    };
    const contentBlockLinks = links && links.length > 0 && (
      <LinksComponent {...{ links: links, onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request, telemetryContent: this.telemetryContent }} />
    );
    const contentBlockText = paragraph && (
      <Msdyn365.RichTextComponent
        text={paragraph}
        className='ms-content-block__text'
        editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
      />
    );
    const contentBlockImage = image && (
      <Msdyn365.Image {...image} {...imageProps} editProps={{ key: this.props.config.image || {}, requestContext: this.props.context.request }} />
    );
    if (
      !contentBlockTitle &&
      !contentBlockText &&
      !contentBlockImage &&
      !contentBlockLinks
    ) {
      this.props.context.telemetry.warning(
        'Content block content is empty, module wont render.'
      );
      return null;
    }

    const contentBlockviewProps = {
      ...this.props,
      title: contentBlockTitle,
      text: contentBlockText,
      image: contentBlockImage,
      links: links,
      useSeoData: useSeoData,
      seoData: this.props.data.seoData?.result,
      useDynamicImages: useDynamicImages,
      dynamicImages: dynamicImages,
      moduleClass: this.props.config.className,
      contentBlockContainer: {
        moduleProps: this.props,
        className: classnames('ms-content-block', className)
      },
      imageContainer: { className: 'ms-content-block__image' },
      detailsContainer: { className: 'ms-content-block__details' },
      contentBlockAnchorTag: {
        tag: 'a',
        className: 'ms-content-block__link',
        role: 'link'
      },
      imageLink: unbxdImageURL && unbxdLandingURL ? unbxdLandingURL : this._getImageLink(),
      imageAriaLabel,
      telemetryContent: this.telemetryContent,
      handleTextChange: this.handleLinkTextChange,
      unbxdBannerHTML: unbxdBannerHTMLRaw,
      unbxdImageURL: unbxdImageURL,
      showServerSideBanner: this.state.showServerSideBanner
    };

    let isUnbxdBannerExist = false;
    if (unbxdBannerHTMLRaw || unbxdImageURL) {
      isUnbxdBannerExist = true;
    }
    let showBanner = false;
    if (!isUnbxdBannerExist && this.props.config.categoryRecordId !== undefined && this.props.config.categoryRecordId !== '') {
      const splitCategoryId = this.props.config.categoryRecordId.split(',');
      if (splitCategoryId.length > 0) {
        splitCategoryId.map((item: string) => {
          if (this.props.context.actionContext.requestContext.url.requestUrl.pathname.includes(item)) {
            showBanner = true;
          }
        });
      }
    }

    if (isShowUnbxdBanner && (showBanner || isUnbxdBannerExist)) {
      return this.props.renderView(contentBlockviewProps) as React.ReactElement;
    } else {
      return null;
    }
  }
  public handleTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);
  public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.paragraph = event.target.value);
  /* Handle link text change*/
  public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
    if (this.props.config.links && this.props.config.links[linkIndex]) {
      this.props.config.links[linkIndex].linkText = event.target.value;
    }
  };
  private _getImageLink(): string | null {
    const { imageLink, links, actionableRegion } = this.props.config;
    if (actionableRegion === region.imageAndLinks) {
      if (imageLink && imageLink.destinationUrl) {
        return imageLink.destinationUrl;
      } else if (links && links.length && links[0].linkUrl) {
        return links[0].linkUrl.destinationUrl;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}

export default ContentBlock;
